import React, { useState } from 'react';

const SignUpForm = ({ onSubmit, loading }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors, setErrors] = useState({});

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    if (score <= 1) return { level: 1, label: 'Weak', color: '#F06F6B' };
    if (score === 2) return { level: 2, label: 'Fair', color: '#F58A54' };
    if (score === 3) return { level: 3, label: 'Good', color: '#68D391' };
    return { level: 4, label: 'Strong', color: '#48BB78' };
  };

  const strength = getPasswordStrength(password);

  const validate = () => {
    const newErrors = {};
    if (!fullName?.trim()) {
      newErrors.fullName = 'Full name is required.';
    }
    if (!email) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!password) {
      newErrors.password = 'Password is required.';
    } else if (password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters.';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password.';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }
    if (!acceptTerms) {
      newErrors.terms = 'You must accept the terms to continue.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validate()) {
      onSubmit({ fullName, email, password });
    }
  };

  const EyeIcon = ({ show }) => show ? (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Full Name
        </label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => { setFullName(e?.target?.value); setErrors(prev => ({ ...prev, fullName: '' })); }}
          placeholder="Your full name"
          className={`w-full px-4 py-3 rounded-xl border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all duration-200 outline-none focus:ring-2 ${
            errors?.fullName
              ? 'border-red-400 focus:ring-red-200' : 'border-gray-200 dark:border-gray-700 focus:border-orange-400 focus:ring-orange-100'
          }`}
        />
        {errors?.fullName && <p className="mt-1.5 text-xs" style={{ color: '#F06F6B' }}>{errors?.fullName}</p>}
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e?.target?.value); setErrors(prev => ({ ...prev, email: '' })); }}
          placeholder="you@example.com"
          className={`w-full px-4 py-3 rounded-xl border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all duration-200 outline-none focus:ring-2 ${
            errors?.email
              ? 'border-red-400 focus:ring-red-200' :'border-gray-200 dark:border-gray-700 focus:border-orange-400 focus:ring-orange-100'
          }`}
        />
        {errors?.email && <p className="mt-1.5 text-xs" style={{ color: '#F06F6B' }}>{errors?.email}</p>}
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Password
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e?.target?.value); setErrors(prev => ({ ...prev, password: '' })); }}
            placeholder="Create a password"
            className={`w-full px-4 py-3 pr-11 rounded-xl border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all duration-200 outline-none focus:ring-2 ${
              errors?.password
                ? 'border-red-400 focus:ring-red-200' :'border-gray-200 dark:border-gray-700 focus:border-orange-400 focus:ring-orange-100'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <EyeIcon show={showPassword} />
          </button>
        </div>
        {password && (
          <div className="mt-2">
            <div className="flex gap-1 mb-1">
              {[1, 2, 3, 4]?.map((i) => (
                <div
                  key={i}
                  className="h-1 flex-1 rounded-full transition-all duration-300"
                  style={{ backgroundColor: i <= strength?.level ? strength?.color : '#E5E7EB' }}
                />
              ))}
            </div>
            <p className="text-xs" style={{ color: strength?.color }}>{strength?.label} password</p>
          </div>
        )}
        {errors?.password && <p className="mt-1.5 text-xs" style={{ color: '#F06F6B' }}>{errors?.password}</p>}
      </div>
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Confirm Password
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e?.target?.value); setErrors(prev => ({ ...prev, confirmPassword: '' })); }}
            placeholder="Confirm your password"
            className={`w-full px-4 py-3 pr-11 rounded-xl border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all duration-200 outline-none focus:ring-2 ${
              errors?.confirmPassword
                ? 'border-red-400 focus:ring-red-200' :'border-gray-200 dark:border-gray-700 focus:border-orange-400 focus:ring-orange-100'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <EyeIcon show={showConfirmPassword} />
          </button>
        </div>
        {errors?.confirmPassword && <p className="mt-1.5 text-xs" style={{ color: '#F06F6B' }}>{errors?.confirmPassword}</p>}
      </div>
      <div className="mb-6">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => { setAcceptTerms(e?.target?.checked); setErrors(prev => ({ ...prev, terms: '' })); }}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-orange-500 focus:ring-orange-400 flex-shrink-0"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            I agree to the{' '}
            <span className="font-medium cursor-pointer hover:opacity-80" style={{ color: '#F06F6B' }}>Terms of Service</span>
            {' '}and{' '}
            <span className="font-medium cursor-pointer hover:opacity-80" style={{ color: '#F06F6B' }}>Privacy Policy</span>
          </span>
        </label>
        {errors?.terms && <p className="mt-1.5 text-xs" style={{ color: '#F06F6B' }}>{errors?.terms}</p>}
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
        style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
      >
        {loading ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Creating account...
          </>
        ) : 'Create Account'}
      </button>
    </form>
  );
};

export default SignUpForm;
